import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  FormHelperText,
} from "@mui/material";
import { Calendar as CalendarIcon, X as ClearIcon } from "lucide-react";
import DateTimePicker from "react-datetime-picker";
import "react-datetime-picker/dist/DateTimePicker.css";
import "react-calendar/dist/Calendar.css";

import { colors, borderRadiusPx, customShadows } from "../../../../theme";

/**
 * CommonDateTimePicker Component
 * Reusable date/time picker component styled consistently with CommonInput and AutoBET design tokens.
 *
 * Features:
 * - Visually identical to CommonInput (background, borders, cardFlat shadow, focus glow, sizing)
 * - Custom themed calendar popup dropdown matching dropdownMenu tokens
 * - Built-in calendar icon with customizable format, icons, and clear button
 * - Can be used standalone, with built-in label/helperText, or wrapped in CommonFormField
 */
const CommonDateTimePicker = ({
  value,
  onChange,
  format = "dd-MM-yy",
  disableClock = true,
  calendarIcon,
  clearIcon = null,
  clearable = false,
  size = "small",
  fullWidth = true,
  width,
  disabled = false,
  readOnly = false,
  error = false,
  id,
  name,
  minDate,
  maxDate,
  placeholder,

  // Optional standalone form control props
  label,
  icon,
  iconColor,
  iconSize = 16,
  required = false,
  requiredColor,
  helperText,
  errorText,
  labelSx = {},
  helperTextSx = {},
  formControlSx = {},

  sx = {},
  ...props
}) => {
  const isError = Boolean(error);
  const resolvedWidth = fullWidth ? "100%" : width;
  const resolvedRequiredColor =
    requiredColor || colors.dashboard?.status?.dangerText || colors.error?.main || "#ef4444";
  const resolvedHelperText =
    typeof error === "string" ? error : isError ? errorText || helperText : helperText;

  const resolvedCalendarIcon =
    calendarIcon !== undefined ? (
      calendarIcon
    ) : (
      <CalendarIcon size={16} color={colors.slate[400]} />
    );

  const resolvedClearIcon =
    clearIcon !== null
      ? clearIcon
      : clearable
      ? <ClearIcon size={14} color={colors.slate[400]} />
      : null;

  const pickerElement = (
    <Box
      id={id ? `${id}-container` : undefined}
      sx={{
        position: "relative",
        bgcolor: disabled ? colors.slate[100] : readOnly ? colors.slate[50] : colors.background.paper,
        borderRadius: borderRadiusPx.xl,
        ...(resolvedWidth ? { width: resolvedWidth } : {}),
        boxShadow: customShadows.cardFlat,
        transition: "all 0.2s ease-in-out",
        border: `1px solid ${isError ? colors.error?.main || "#ef4444" : colors.neutral.border}`,
        minHeight: size === "small" ? "42px" : "48px",
        px: 1.5,
        display: "flex",
        alignItems: "center",
        boxSizing: "border-box",
        cursor: disabled ? "not-allowed" : "pointer",
        "&:hover": {
          borderColor: isError
            ? colors.error?.dark || "#dc2626"
            : colors.neutral.borderHover || colors.slate[300],
        },
        "&:focus-within": {
          borderColor: isError
            ? colors.error?.main || "#ef4444"
            : colors.primary.main,
          boxShadow: isError
            ? "0 4px 12px rgba(239, 68, 68, 0.15)"
            : `0 4px 12px ${colors.primary.subtle}`,
        },
        "& .react-datetime-picker": {
          width: "100%",
          fontFamily: "inherit",
          display: "inline-flex",
        },
        "& .react-datetime-picker--disabled": {
          backgroundColor: "transparent",
          opacity: 0.7,
        },
        "& .react-datetime-picker__wrapper": {
          border: "none !important",
          width: "100%",
          display: "flex",
          alignItems: "center",
          backgroundColor: "transparent",
        },
        "& .react-datetime-picker__inputGroup": {
          display: "flex",
          alignItems: "center",
          flexGrow: 1,
          py: size === "small" ? 1 : 1.25,
          minHeight: size === "small" ? "38px" : "44px",
          boxSizing: "border-box",
        },
        "& .react-datetime-picker__inputGroup__input": {
          fontSize: "14px",
          fontWeight: 500,
          color: colors.text.primary,
          fontFamily: "inherit",
          border: "none !important",
          outline: "none !important",
          boxShadow: "none !important",
          background: "transparent !important",
          boxSizing: "content-box",
          padding: "0 2px",
          "&:focus": {
            backgroundColor: `${colors.primary.lighter} !important`,
            color: `${colors.primary.main} !important`,
            borderRadius: "4px",
          },
          "&:disabled": {
            color: colors.text.disabled,
          },
        },
        "& .react-datetime-picker__inputGroup__divider": {
          color: colors.slate[400],
          padding: "0 2px",
          fontSize: "14px",
          userSelect: "none",
        },
        "& .react-datetime-picker__inputGroup__leadingZero": {
          color: colors.text.primary,
          fontSize: "14px",
          fontWeight: 500,
        },
        "& .react-datetime-picker__button": {
          border: "none !important",
          background: "transparent !important",
          padding: "4px 6px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          borderRadius: "6px",
          color: colors.slate[400],
          transition: "all 0.15s ease-in-out",
          "&:hover": {
            color: colors.primary.main,
            backgroundColor: colors.slate[100],
          },
          "&:enabled:focus": {
            backgroundColor: colors.primary.lighter,
            color: colors.primary.main,
          },
          "& svg": {
            display: "block",
          },
        },
        "& .react-datetime-picker__calendar": {
          zIndex: 1300,
          width: "auto !important",
          maxWidth: "100%",
        },
        "& .react-datetime-picker__calendar--closed": {
          display: "none",
        },
        "& .react-calendar": {
          width: "320px",
          maxWidth: "100%",
          backgroundColor: colors.background.paper,
          border: `1px solid ${colors.dashboard?.cardBorder || colors.neutral.border}`,
          borderRadius: borderRadiusPx.xl,
          boxShadow: customShadows.dropdownMenu || "0 10px 30px rgba(0, 0, 0, 0.12)",
          fontFamily: "inherit",
          p: 1.5,
          lineHeight: "1.25em",
        },
        "& .react-calendar__navigation": {
          display: "flex",
          height: "40px",
          mb: 1,
          "& button": {
            minWidth: "40px",
            background: "none",
            fontSize: "14px",
            fontWeight: 600,
            color: colors.slate[700],
            border: "none",
            borderRadius: borderRadiusPx.md,
            cursor: "pointer",
            transition: "all 0.15s ease-in-out",
            "&:hover": {
              backgroundColor: colors.slate[100],
              color: colors.slate[900],
            },
            "&:disabled": {
              opacity: 0.4,
              cursor: "not-allowed",
            },
            "&.react-calendar__navigation__label": {
              flexGrow: 1,
            },
          },
        },
        "& .react-calendar__month-view__weekdays": {
          textAlign: "center",
          textTransform: "uppercase",
          fontWeight: 600,
          fontSize: "11px",
          color: colors.slate[400],
          mb: 1,
          "& abbr": {
            textDecoration: "none",
          },
        },
        "& .react-calendar__tile": {
          maxWidth: "100%",
          textAlign: "center",
          padding: "8px 6px",
          background: "none",
          border: "none",
          borderRadius: borderRadiusPx.md,
          fontSize: "13px",
          fontWeight: 500,
          color: colors.slate[700],
          cursor: "pointer",
          transition: "all 0.15s ease-in-out",
          "&:hover": {
            backgroundColor: colors.slate[100],
            color: colors.slate[900],
          },
          "&:disabled": {
            opacity: 0.35,
            cursor: "not-allowed",
          },
        },
        "& .react-calendar__tile--now": {
          backgroundColor: colors.slate[100],
          color: colors.primary.main,
          fontWeight: 700,
        },
        "& .react-calendar__tile--active, & .react-calendar__tile--hasActive": {
          backgroundColor: `${colors.primary.main} !important`,
          color: `${colors.primary.contrastText} !important`,
          fontWeight: 600,
          boxShadow: "0 2px 6px rgba(37, 99, 235, 0.3)",
        },
        "& .react-calendar__month-view__days__day--neighboringMonth": {
          color: colors.slate[300],
        },
        ...sx,
      }}
    >
      <DateTimePicker
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        format={format}
        disableClock={disableClock}
        calendarIcon={resolvedCalendarIcon}
        clearIcon={resolvedClearIcon}
        disabled={disabled}
        readOnly={readOnly}
        minDate={minDate}
        maxDate={maxDate}
        placeholder={placeholder}
        {...props}
      />
    </Box>
  );

  if (label || helperText || errorText) {
    return (
      <Box
        sx={{
          position: "relative",
          width: resolvedWidth || "100%",
          ...formControlSx,
        }}
      >
        {label && (
          <Box
            component="label"
            htmlFor={id}
            sx={{
              position: "absolute",
              top: -8,
              left: 12,
              bgcolor: colors.background.paper,
              px: 0.6,
              fontSize: "12px",
              fontWeight: 500,
              color: isError ? colors.error?.main || "#ef4444" : colors.slate[600],
              zIndex: 2,
              lineHeight: 1,
              pointerEvents: "none",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              ...labelSx,
            }}
          >
            {icon && (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  color: iconColor || colors.slate[500],
                  "& svg": { width: iconSize, height: iconSize },
                }}
              >
                {icon}
              </Box>
            )}
            {label}
            {required && (
              <Box
                component="span"
                sx={{
                  color: resolvedRequiredColor,
                  ml: 0.25,
                }}
              >
                *
              </Box>
            )}
          </Box>
        )}

        {pickerElement}

        {resolvedHelperText && (
          <FormHelperText
            error={isError}
            sx={{
              fontSize: "12px",
              mt: 0.5,
              mx: 1.5,
              color: isError
                ? resolvedRequiredColor
                : colors.slate[500],
              ...helperTextSx,
            }}
          >
            {resolvedHelperText}
          </FormHelperText>
        )}
      </Box>
    );
  }

  return pickerElement;
};

CommonDateTimePicker.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  format: PropTypes.string,
  disableClock: PropTypes.bool,
  calendarIcon: PropTypes.node,
  clearIcon: PropTypes.node,
  clearable: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium"]),
  fullWidth: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  id: PropTypes.string,
  name: PropTypes.string,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  placeholder: PropTypes.string,

  // Standalone label & helper props
  label: PropTypes.node,
  icon: PropTypes.node,
  iconColor: PropTypes.string,
  iconSize: PropTypes.number,
  required: PropTypes.bool,
  requiredColor: PropTypes.string,
  helperText: PropTypes.node,
  errorText: PropTypes.node,
  labelSx: PropTypes.object,
  helperTextSx: PropTypes.object,
  formControlSx: PropTypes.object,

  sx: PropTypes.object,
};

export default CommonDateTimePicker;
